// The prerendered homepage's two client-only shims, in a module of their own so a homepage rewrite can't lose them. Node-prerender-safe: react + react-router only — no store/Amplify/@shared (routes.ts), and nothing URL- or storage-derived is read during render, only inside the effect (same reason series-migration.tsx decides everything post-mount: `/` is baked with empty search params and no localStorage, so reading either at render hydration-mismatches).
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

// Amplify's own storage, read directly: the SPA's `authenticated` store flag drags zustand + Amplify into the prerender graph. Key format `CognitoIdentityServiceProvider.<clientId>.LastAuthUser`, cleared on sign-out.
const LAST_USER_SUFFIX = '.LastAuthUser';

/** Module-scope, deliberately: a stale/expired token is a false positive, and without a one-shot guard home → /app/j → the SPA's AuthRedirect sees `authenticated === false` → navigate("/") → home remounts → forwards again, an infinite client-side loop. */
let forwarded = false;

/** Test-only: the guard above outlives a test's unmount, so cases would run order-dependent. */
export function __resetHomeShims() { forwarded = false; nav.hard = hardRedirect; }

const hardRedirect = (url: string) => { window.location.replace(url); };
/** Seam, test-only: jsdom implements no navigation and makes `window.location` unforgeable (no spyOn, no defineProperty), so the one branch that must be a FULL-document load is only observable through this. */
export const nav = { hard: hardRedirect };

/**
 * Cognito's OAuth return, LEGACY path. Prod `callbackUrls` now list `https://gnothiai.com/auth` first (infra/gnothi/core.ts), so a new sign-in lands straight on the splat-served `/auth`; the bare-origin entry stays registered one release, for an `/authorize` already in flight when the deploy landed. That return still arrives at `/?code=..&state=..` — this prerendered marketing route, whose entire point is that it pulls in no Amplify (header above). Amplify only configures under the `*` splat ($.client.tsx → Setup/index.tsx), and a splat cannot match the index path, so without this the return reaches nothing at all. Delete this shim together with those bare-origin entries.
 */
function isOAuthReturn(params: URLSearchParams): boolean {
  return (params.has('code') && params.has('state')) || params.has('error');
}

/** True only when Amplify has both a last-authed username AND that user's cached idToken. */
function hasCognitoSession(): boolean {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.endsWith(LAST_USER_SUFFIX)) continue;
      const username = localStorage.getItem(key);
      if (username && localStorage.getItem(`${key.slice(0, -LAST_USER_SUFFIX.length)}.${username}.idToken`)) return true;
    }
  } catch { /* private mode throws on localStorage access */ }
  return false;
}

/**
 * Post-mount only, so a crawler executes neither branch and indexes the full prerendered marketing HTML. A logged-out visitor sees marketing with no redirect; a logged-in one sees a brief marketing paint, then the app — that flash is accepted.
 * Shim 0 (FIRST, before the session check — a stale `LastAuthUser`+`idToken` pair from an earlier sign-in otherwise sends shim A to `/app/j` and drops `?code=` on the floor, which is a half-authed session with an empty dashboard): an OAuth return → hand it to a route the SPA owns.
 * `window.location.replace`, never `navigate()`: Amplify reads `window.location.href` inside the listener it fires at `Amplify.configure()` time (attemptCompleteOAuthFlow.mjs), which is module-eval of Setup/index.tsx — a client-side navigation races the lazy route module and would hand it the pre-navigation URL. `window.location.search` verbatim, not a re-serialized `params`, so `code`/`state` reach Cognito byte-identical. The PKCE verifier and in-flight flag live in localStorage (Amplify's `defaultStorage`), so a full document load keeps them. The exchange itself still fails for such a code: `attemptCompleteOAuthFlow` recomputes `redirect_uri` from the client's `redirectSignIn` — now `origin + '/auth'` (src/utils/config.ts) — not from the value the code was issued for, so Cognito answers `invalid_grant`. The bounce is worth keeping anyway, because it delivers that failure to a route that mounts Amplify: the visitor gets AuthLanding's expired-code retry instead of a silent marketing page.
 * Shim A: logged in at `/` → into the app. Shim B (only if A didn't fire): the legacy `/?auth=<tab>` CTA link → the SPA's real `/auth` route.
 */
export function useHomeShims() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  useEffect(() => {
    if (forwarded) return;
    if (isOAuthReturn(params)) {
      forwarded = true;
      nav.hard(`/auth${window.location.search}`);
      return;
    }
    if (hasCognitoSession()) {
      forwarded = true;
      navigate('/app/j', { replace: true });
      return;
    }
    const tab = params.get('auth');
    if (tab) {
      forwarded = true;
      // `encodeURIComponent`: `params.get` already URL-decoded, so `?auth=x%26foo%3Dbar` would otherwise interpolate a second query param into the redirect.
      navigate(`/auth?tab=${encodeURIComponent(tab)}`, { replace: true });
    }
  }, []);
}
